import { useState, useEffect, useCallback } from 'react';
import { stockService } from '../services/api';
import { stockCache } from '../services/stockCache';

/**
 * Hook personnalisé pour gérer le cache de stock avec chargement automatique
 * @param {string} shopId - ID du magasin/client
 * @param {string} token - Token d'authentification
 * @returns {Object} { stockData, loading, error, refreshStock, searchProducts, cacheAge }
 */
export const useStockCache = (shopId, token) => {
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheAge, setCacheAge] = useState(null);

  /**
   * Charge les données de stock depuis l'API
   */
  const loadStock = useCallback(async () => {
    if (!shopId || !token) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await stockService.getStock(shopId, token);
      setStockData(data);
      stockCache.set(shopId, data);
      setCacheAge(0);
    } catch (err) {
      console.error('Erreur lors du chargement du stock:', err);
      setError(err.message || 'Erreur lors du chargement du stock');
    } finally {
      setLoading(false);
    }
  }, [shopId, token]);

  /**
   * Rafraîchit les données de stock (force le rechargement)
   */
  const refreshStock = useCallback(async () => {
    stockCache.clear(shopId);
    await loadStock();
  }, [shopId, loadStock]);

  /**
   * Recherche dans les données en cache
   * @param {string} query - Terme de recherche
   * @returns {Array} Liste des produits filtrés
   */
  const searchProducts = useCallback((query) => {
    if (!stockData || !query || query.trim().length < 2) {
      return [];
    }

    const upperQuery = query.toUpperCase().trim();
    const filteredProducts = stockData.StockLineList.filter((product) => {
      return (
        product.ProductSku?.includes(upperQuery) ||
        product.Gencod?.includes(query) ||
        product.ProductClientSku?.includes(upperQuery)
      );
    });

    return filteredProducts;
  }, [stockData]);

  // Charger les données au montage ou quand shopId/token change
  useEffect(() => {
    if (!shopId || !token) {
      return;
    }

    // Vérifier si on a des données en cache valides
    const cachedData = stockCache.get(shopId);
    
    if (cachedData) {
      setStockData(cachedData);
      setCacheAge(stockCache.getAge(shopId));
      setError(null);
      
      // Si le cache est proche de l'expiration, le rafraîchir en arrière-plan
      if (stockCache.needsRefresh(shopId)) {
        loadStock();
      }
    } else {
      // Pas de cache, charger les données
      loadStock();
    }
  }, [shopId, token, loadStock]);

  // Mettre à jour l'âge du cache périodiquement
  useEffect(() => {
    if (!shopId || !stockData) return;

    const interval = setInterval(() => {
      const age = stockCache.getAge(shopId);
      setCacheAge(age);

      // Si le cache est expiré, le rafraîchir
      if (stockCache.needsRefresh(shopId)) {
        loadStock();
      }
    }, 60000); // Vérifier toutes les minutes

    return () => clearInterval(interval);
  }, [shopId, stockData, loadStock]);

  return {
    stockData,
    loading,
    error,
    refreshStock,
    searchProducts,
    cacheAge,
    isCached: stockCache.isValid(shopId),
  };
};

export default useStockCache;

